import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Upload, FileText, RefreshCw, UserCheck, ExternalLink, ClipboardCheck, ArrowRight, MapPin } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import PreparePanel from "@/components/app/prepare/PreparePanel";
import { ReadinessBadge, SeverityBadge, DocStatusBadge } from "@/components/StatusBadge";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const TABS = ["Requirements", "Prepare", "Documents", "Findings", "Dossier"];

export default function CaseView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Requirements");
  const [busy, setBusy] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  const load = useCallback(() => {
    api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <AppShell title="Case"><p className="text-muted">This case could not be loaded.</p></AppShell>;
  if (!data) return <AppShell><div className="h-40 animate-pulse rounded-md bg-white" /></AppShell>;

  const { case: c, corridor, checklist, findings, requirement_set } = data;
  const openFindings = findings.filter((f) => f.status === "open");
  const readiness = c.readiness || {};

  const runChecks = async () => {
    setBusy(true);
    try { await api.post(`/cases/${id}/run-checks`); toast.success("Checks complete"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestReview = async () => {
    setBusy(true);
    try { await api.post(`/reviews/request/${id}`); toast.success("Human review requested"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (code, file) => {
    if (!file) return;
    setUploadingFor(code);
    const fd = new FormData();
    fd.append("case_id", id); fd.append("doc_type", code); fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setUploadingFor(null); }
  };

  const resolveFinding = async (fid, action) => {
    try { await api.post(`/cases/${id}/findings/${fid}/resolve`, { action }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <AppShell back={{ to: "/app", label: "All cases" }}>
      {/* Header */}
      <div className="rounded-md border border-hborder bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-md bg-cream"><MapPin size={20} className="text-coral" /></span>
            <div>
              <h1 className="font-serif text-2xl tracking-tight text-heading">{corridor?.destination}</h1>
              <p className="text-sm text-muted">{data.visa_category_name} · {data.profile?.full_name}</p>
            </div>
          </div>
          <ReadinessBadge state={c.readiness_state} size="lg" testId="case-readiness" />
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Stat label="Required complete" value={`${readiness.required_satisfied || 0} / ${readiness.required_total || 0}`} />
          <Stat label="Open findings" value={readiness.open_findings || 0} tone={openFindings.length ? "warn" : "ok"} />
          <Stat label="Review" value={c.review_state ? c.review_state.replace("_", " ") : "Not requested"} />
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={runChecks} disabled={busy} className="inline-flex items-center gap-2 rounded bg-navy px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="run-checks-btn">
            <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Run checks
          </button>
          {!["requested", "in_review"].includes(c.review_state) && (
            <button onClick={requestReview} disabled={busy} className="inline-flex items-center gap-2 rounded border border-hborder bg-white px-5 py-2.5 text-sm font-semibold text-navy disabled:opacity-60" data-testid="request-review-btn">
              <UserCheck size={15} /> Request human review
            </button>
          )}
        </div>
        {c.review_state === "returned" && (
          <div className="mt-4 rounded-lg border border-coral/30 bg-orange-50 p-3 text-sm text-coral" data-testid="review-returned-banner">Your reviewer returned this case with items to correct. Review the findings below.</div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 overflow-x-auto rounded border border-hborder bg-white p-1 hv-scroll-thin" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap rounded px-4 py-2 text-sm font-medium transition-colors ${tab === t ? "bg-navy text-white" : "text-muted hover:text-navy"}`}>
            {t}{t === "Findings" && openFindings.length > 0 ? ` (${openFindings.length})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "Requirements" && (
          <div className="space-y-3" data-testid="requirements-panel">
            <p className="text-sm text-muted">Requirement set v{requirement_set?.version} · last verified {requirement_set?.last_verified_date}</p>
            {checklist.map((r) => (
              <div key={r.code} className="rounded-md border border-hborder bg-white p-4" data-testid={`req-${r.code}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {r.satisfied ? <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={20} /> : <AlertTriangle className="mt-0.5 shrink-0 text-brandyellow" size={20} />}
                    <div>
                      <p className="font-medium text-heading">{r.title} {r.required && <span className="ml-1 rounded bg-cream px-2 py-0.5 text-xs text-coral">Required</span>}</p>
                      <p className="mt-1 text-sm text-muted">{r.description}</p>
                      {r.copies && <p className="mt-1 text-xs font-medium text-navy" data-testid={`copies-${r.code}`}>Submit: {r.copies}</p>}
                      {(r.instructions || []).length > 0 && <ul className="mt-1.5 list-disc pl-5 text-xs text-muted">{r.instructions.map((t) => <li key={t}>{t}</li>)}</ul>}
                      {r.source?.url && <a href={r.source.url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-tblue hover:underline">{r.source.name} <ExternalLink size={11} /></a>}
                      {r.documents.map((d) => (
                        <Link key={d.id} to={`/app/cases/${id}/documents/${d.id}`} className="mt-2 flex items-center gap-2 text-sm text-navy hover:underline" data-testid={`req-doc-${d.id}`}>
                          <FileText size={14} /> {d.original_filename || "Document"} <DocStatusBadge status={d.status} />
                        </Link>
                      ))}
                    </div>
                  </div>
                  {(r.kind === "form" || r.kind === "letter") ? (
                    <button onClick={() => setTab("Prepare")} className="inline-flex items-center gap-2 rounded border border-navy/25 bg-white px-4 py-2 text-sm font-medium text-navy hover:border-navy" data-testid={`prepare-${r.code}`}>Fill in Prepare</button>
                  ) : r.kind === "info" ? null : (
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-hborder bg-white px-4 py-2 text-sm font-medium text-navy hover:border-navy" data-testid={`upload-${r.code}`}>
                    <Upload size={14} /> {uploadingFor === r.code ? "Uploading…" : r.satisfied ? "Replace" : "Upload"}
                    <input type="file" className="hidden" onChange={(e) => upload(r.code, e.target.files[0])} accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" />
                  </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Prepare" && <PreparePanel caseId={id} visa={c.visa_category} process={data.process} onChanged={load} />}

        {tab === "Documents" && (
          <div className="space-y-3" data-testid="documents-panel">
            {data.documents.length === 0 ? (
              <p className="rounded-md border border-dashed border-hborder bg-white p-8 text-center text-sm text-muted">No documents uploaded yet. Upload evidence from the Requirements tab.</p>
            ) : data.documents.map((d) => {
              const req = checklist.find((r) => r.code === d.doc_type);
              return (
                <Link key={d.id} to={`/app/cases/${id}/documents/${d.id}`} className="flex items-center justify-between rounded-md border border-hborder bg-white p-4 " data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-lg bg-softcream"><FileText size={18} className="text-navy" /></span>
                    <div>
                      <p className="font-medium text-heading">{req?.title || d.doc_type}</p>
                      <p className="text-xs text-muted">{d.original_filename} · v{(d.versions || []).length}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2"><DocStatusBadge status={d.status} /><ArrowRight size={15} className="text-muted" /></div>
                </Link>
              );
            })}
          </div>
        )}

        {tab === "Findings" && (
          <div className="space-y-3" data-testid="findings-panel">
            {findings.length === 0 ? (
              <p className="rounded-md border border-dashed border-hborder bg-white p-8 text-center text-sm text-muted">No findings yet. Run checks after adding your documents to see what needs attention.</p>
            ) : findings.map((f) => (
              <div key={f.id} className={`rounded-md border bg-white p-4 ${f.status !== "open" ? "opacity-70" : ""}`} data-testid={`finding-${f.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <SeverityBadge severity={f.severity} />
                    {f.status !== "open" && <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{f.status}</span>}
                  </div>
                </div>
                <p className="mt-2 font-medium text-heading">{f.title}</p>
                <p className="mt-1 text-sm text-muted">{f.explanation}</p>
                <div className="mt-3 flex gap-2">
                  {f.status === "open" ? (
                    <>
                      <button onClick={() => resolveFinding(f.id, "resolve")} className="rounded bg-navy px-4 py-1.5 text-xs font-semibold text-white" data-testid={`resolve-${f.id}`}>Mark resolved</button>
                      <button onClick={() => resolveFinding(f.id, "dismiss")} className="rounded border border-hborder px-4 py-1.5 text-xs font-medium text-navy" data-testid={`dismiss-${f.id}`}>Not applicable</button>
                    </>
                  ) : (
                    <button onClick={() => resolveFinding(f.id, "reopen")} className="rounded border border-hborder px-4 py-1.5 text-xs font-medium text-navy" data-testid={`reopen-${f.id}`}>Reopen</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Dossier" && <DossierPanel caseId={id} />}
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "warn" ? "text-coral" : tone === "ok" ? "text-emerald-600" : "text-heading";
  return (
    <div className="rounded-md border border-hborder bg-softcream p-4">
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function DossierPanel({ caseId }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/cases/${caseId}/dossier`).then((r) => setD(r.data)).catch(() => {}); }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="h-40 animate-pulse rounded-md bg-white" />;

  const generate = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/dossier/generate`); toast.success("Dossier prepared"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="dossier-panel">
      <div className="rounded-md border border-hborder bg-white p-5">
        <div className="flex items-center gap-2"><ClipboardCheck className="text-tblue" size={20} /><h3 className="font-semibold text-heading">Preparation dossier</h3></div>
        <p className="mt-1 text-sm text-muted">A user-approved package of your documents and case details, tied to requirement set v{d.requirement_set_version}. It never includes unconfirmed AI facts.</p>
        <div className="mt-4 space-y-2">
          {d.checklist.map((item) => (
            <div key={item.code} className="flex items-center gap-2.5 text-sm">
              {item.satisfied ? <CheckCircle2 size={16} className="text-emerald-600" /> : <AlertTriangle size={16} className="text-brandyellow" />}
              <span className={item.satisfied ? "text-heading" : "text-muted"}>{item.title}</span>
              {item.required && <span className="text-xs text-coral">Required</span>}
            </div>
          ))}
        </div>
        {d.dossier ? (
          <div className="mt-5 rounded-lg border border-emerald-300 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-800">Dossier v{d.dossier.version} prepared</p>
            <p className="mt-0.5 text-xs text-emerald-700">Generated {new Date(d.dossier.generated_at).toLocaleString()}. You can continue through the official process.</p>
          </div>
        ) : (
          <button onClick={generate} disabled={busy || !d.can_generate} className="mt-5 inline-flex items-center gap-2 rounded bg-brandyellow px-6 py-3 text-sm font-semibold text-heading disabled:opacity-50" data-testid="generate-dossier-btn">
            {busy ? "Preparing…" : "Prepare dossier"}
          </button>
        )}
        {!d.can_generate && !d.dossier && <p className="mt-2 text-xs text-muted">Add all required documents and resolve open findings to prepare your dossier.</p>}
      </div>
    </div>
  );
}
